import React from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-hot-toast';
import Text from '@/utils/responsiveText';
import CustomInput from './CustomInput';
import { useLogoutMutation, useOwnerChangePasswordMutation } from '@/store/api/authApi';
import { passwordSchema } from '@/utils/validation';
import { clearUser } from '@/store/slices/userSlice';
import { logout as clearAuth } from '@/store';
import { useDispatch } from 'react-redux';
import apiSlice from '@/store/api/apiSlice';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';

type FormValues = z.infer<typeof passwordSchema>;

const fields = [
  { name: 'oldPassword', label: 'Current Password', placeholder: 'Enter current password' },
  { name: 'newPassword', label: 'New Password', placeholder: 'Enter new password' },
  { name: 'confirmPassword', label: 'Confirm Password', placeholder: 'Confirm new password' },
];
interface ChangePasswordProps {
  close?: () => void;
  title?: boolean;
  inputClass?: string;
  lableClass?: string;
  role?: string;
}
export const ChangePassword = ({
  close,
  title = false,
  inputClass = 'bg-white/10 placeholder:text-black w-full',
  lableClass = 'font-mediun text-base text-whiy',
  role,
}: ChangePasswordProps) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { oldPassword: '', newPassword: '', confirmPassword: '' },
    mode: 'onChange',
  });

  const [changePassword, { isLoading }] = useOwnerChangePasswordMutation();
  const [OwnerChangePassword, { isLoading: isOwnerLoading }] = useOwnerChangePasswordMutation();
  const [logout, { isLoading: logoutLoading }] = useLogoutMutation();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    try {
      let res;

      if (role === 'OWNER') {
        res = await OwnerChangePassword({
          oldPassword: data.oldPassword,
          newPassword: data.newPassword,
        }).unwrap();
      } else {
        res = await changePassword({
          oldPassword: data.oldPassword,
          newPassword: data.newPassword,
        }).unwrap();
      }
      if (res.success || res.status === 'success' || res.status) {
        toast.success('Password changed successfully');
        await logout().unwrap();
        dispatch(clearAuth());
        dispatch(clearUser());
        apiSlice.util.resetApiState();
        navigate('/login', { replace: true });
        close?.();
      }
    } catch (err: any) {
      toast.error(err?.data?.message || 'Failed to change password');
    }
  };

  return (
    <div className="max-w-2xl mx-auto rounded-2xl border border-green bg-light-green px-5 py-6 text-textcolor shadow-green-shadow sm:px-10 lg:px-20">
      <div className="flex-start gap-3 py-2">
        <img src="/images/lock.png" className="w-10" />
        <div className="flex flex-col gap-0.5">
          <span className=" text-xl">Change Password</span>
          <span className="text-gray">Update password for enhanced account security.</span>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className=" space-y-2 py-2">
        {fields.map((field) => (
          <CustomInput
            key={field.name}
            name={field.name as keyof FormValues}
            label={field.label}
            placeholder={field.placeholder}
            type="password"
            control={control}
            error={errors[field.name as keyof FormValues]}
            className="w-full "
            inputClassName={`${inputClass} placeholder:text-gray-700`}
            labelClassName={`${lableClass}`}
          />
        ))}
        <Button type="submit" disabled={isLoading} className="w-full">
          {isLoading ? 'Changing...' : 'Change Password'}
        </Button>
      </form>
    </div>
  );
};
